// "unmask" is used to let FullStory capture the component
//   see: https://help.fullstory.com/hc/en-us/articles/360044349073-FullStory-Private-by-Default#01FHE22KSQ9J1N7DZ685G3M872
import { useRef, type InputHTMLAttributes, type ReactElement } from 'react';
import { formGapSizes } from '~/components/layout/templates/FormLayout';
import type { ChipTagProps } from '~/components/new/Chip';
import ControlCard from '~/components/ui/ControlCard';
import Form from '~/components/ui/Form';
import type { IconNames } from '~/components/ui/Icon';
import Icon, { RemixIconNames } from '~/components/ui/Icon';
import Label from '~/components/ui/Label';
import Tooltip from '~/components/ui/Tooltip';
import type { DataTestAttributes } from '~/utils/qa';
import { classNames } from '~/utils/styles';

export type ChoiceOption = {
    label: string;
    value: string | number | boolean;
    description?: string;
    disabled?: boolean;
    hint?: string | ((disabled: boolean) => string);
    tag?: string;
    tagColor?: ChipTagProps['color'];
    tagIcon?: IconNames;
    'data-test-name'?: string;
    'data-test-id'?: string;
};

export type ChoiceProps = {
    name: string;
    options: ChoiceOption[];
    value?: ChoiceOption['value'];
    onChange?: (value: ChoiceOption['value']) => void;
    disabled?: boolean;
    inline?: boolean;
    inlineGap?: keyof typeof formGapSizes;
    plain?: boolean;
    unmask?: boolean;
    className?: string;
    label?: string | ReactElement<typeof Label>;
    helpText?: string;
} & DataTestAttributes;

export type RadioInputProps = {
    unmask?: boolean;
    className?: string;
    checked?: boolean;
    onChange?: (value: boolean) => void;
    'data-test-name'?: string;
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'checked'> &
    DataTestAttributes;

export function RadioInput({
    onChange,
    unmask = false,
    className = '',
    checked,
    'data-test-component': testComponent = 'ui/forms/Choice/RadioInput',
    'data-test-name': testName,
    ...props
}: RadioInputProps) {
    const inputRef = useRef<HTMLInputElement>(null);
    return (
        <div
            className={`radio-input ${className}`}
            onClick={(e) => {
                // ignore input’s own click when bubbles back up after inputRef.current?.click()
                // ignore also the click when readOnly is set. Notice this is not the same readonly for the input, this
                // is to make the element to display a state but not to be interactive.
                if (e.target === inputRef.current || props.readOnly) return;
                inputRef.current?.click();
            }}
            data-test-component={testComponent}
            data-test-name={testName}
            data-test-selected={checked}>
            {checked ? (
                <Icon
                    icon={RemixIconNames.RiRadioButtonFill}
                    className='radio-input-icon-active'
                />
            ) : (
                <Icon
                    icon={RemixIconNames.RiCircleLine}
                    className='radio-input-icon-inactive'
                />
            )}

            <input
                {...props}
                ref={inputRef}
                type='radio'
                className={unmask ? 'fs-unmask' : ''}
                onChange={() => onChange?.(!checked)}
                checked={checked}
                readOnly
            />
        </div>
    );
}

export default function Choice({
    name,
    options,
    value,
    onChange,
    disabled = false,
    inline = false,
    inlineGap = 'medium',
    plain = false,
    unmask = false,
    className = '',
    label,
    helpText,
    'data-test-component': testComponent = 'ui/forms/Choice',
    'data-test-name': testName,
}: ChoiceProps) {
    return (
        <div
            className={classNames({
                'control-list': true,
                'choice-input-plain': Boolean(plain),
                [className]: Boolean(className),
            })}
            data-test-component={testComponent}
            data-test-name={testName}>
            <div className='control-list-header'>
                {typeof label === 'string' ? <Label>{label}</Label> : label}
            </div>
            <div
                className={classNames({
                    'control-list-content': true,
                    [`choice-input-inline gap-${formGapSizes[inlineGap]}`]: Boolean(inline),
                })}>
                {options.map((o) => {
                    const hint = typeof o.hint === 'function' ? o.hint?.(disabled) : o.hint;

                    return (
                        <Tooltip
                            key={`${name}-${o.value}`}
                            title={hint || ''}>
                            <div className='flex w-full mx-0'>
                                <div
                                    className='choice-input-elem'
                                    data-test-component='ui/forms/Choice/ChoiceInputElement'
                                    data-test-name={o['data-test-name'] || o.label}>
                                    <ControlCard
                                        title={o.label}
                                        subtitle={o.description}
                                        tag={o.tag}
                                        tagColor={o.tagColor}
                                        tagIcon={o.tagIcon}
                                        disabled={disabled || o.disabled}
                                        onClick={() => {
                                            if (!disabled && !o.disabled) {
                                                onChange?.(o.value);
                                            }
                                        }}
                                        inputComponent={
                                            <RadioInput
                                                name={name}
                                                id={`${name}-${o.value}`}
                                                className={unmask ? 'fs-unmask' : ''}
                                                disabled={o.disabled}
                                                checked={o.value === value}
                                                value={String(o.value)}
                                                readOnly
                                            />
                                        }
                                    />
                                </div>
                            </div>
                        </Tooltip>
                    );
                })}
                {helpText && <Form.Help>{helpText}</Form.Help>}
            </div>
        </div>
    );
}

export const yesOrNoOptions = [
    { label: 'Yes', value: true },
    { label: 'No', value: false },
];

export function YesOrNo(props: Omit<ChoiceProps, 'options'>) {
    return (
        <Choice
            {...props}
            options={yesOrNoOptions}
            data-test-component='ui/forms/Choice/YesOrNo'
        />
    );
}
